/** 解析 businessParams JSON（与创作列表 parseBusinessParams 保持一致） */

const HTTP_URL_PATTERN = /^https?:\/\//i;

export function extractScalarText(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value.map((x) => extractScalarText(x)).filter(Boolean).join('/');
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const candidate = obj.value ?? obj.label ?? obj.name ?? obj.text ?? obj.title;
    if (candidate !== undefined && candidate !== null) {
      return extractScalarText(candidate);
    }
  }
  return String(value).trim();
}

export function extractTextValue(value: unknown): string {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (
      (trimmed.startsWith('[') && trimmed.endsWith(']')) ||
      (trimmed.startsWith('{') && trimmed.endsWith('}'))
    ) {
      try {
        return extractTextValue(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }
    return trimmed;
  }
  return extractScalarText(value);
}

export function parseBusinessParamsObject(raw: string): Record<string, unknown> | null {
  try {
    let parsed: unknown = JSON.parse(raw);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    if (Array.isArray(parsed)) {
      const fromArray: Record<string, unknown> = {};
      for (const item of parsed) {
        if (!item || typeof item !== 'object') continue;
        const row = item as Record<string, unknown>;
        const entryKey = row.componentKey ?? row.key ?? row.name;
        if (typeof entryKey === 'string' && entryKey.trim()) {
          fromArray[entryKey.trim()] = row.value ?? row.val ?? row.data;
        }
      }
      return Object.keys(fromArray).length > 0 ? fromArray : null;
    }
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function parseBusinessParamsJson(raw?: string | null): Record<string, unknown> | null {
  const text = raw?.trim();
  if (!text) return null;
  return parseBusinessParamsObject(text);
}

export function getParsedEntry(
  parsed: Record<string, unknown>,
  componentKey: string,
): unknown {
  if (componentKey in parsed) return parsed[componentKey];
  const lower = componentKey.trim().toLowerCase();
  for (const [k, v] of Object.entries(parsed)) {
    if (k.trim().toLowerCase() === lower) return v;
  }
  return undefined;
}

export function isRefImageComponentKey(key: string): boolean {
  const k = key.trim();
  return (
    /^bpImage$/i.test(k) ||
    /^reference_?images?$/i.test(k) ||
    /^ref_?images?$/i.test(k) ||
    /^(参考图|参考图片)$/.test(k)
  );
}

export function isPromptComponentKey(key: string): boolean {
  const k = key.trim();
  return (
    /^bpPrompt$/i.test(k) ||
    /^prompt$/i.test(k) ||
    /^user_?prompt$/i.test(k) ||
    /^(提示词|文案)$/.test(k) ||
    /^description$/i.test(k) ||
    /^caption$/i.test(k) ||
    /^text$/i.test(k) ||
    /^content$/i.test(k)
  );
}

export function isUrlOnlyPrefillValue(value: unknown): boolean {
  const urls = coercePrefillUrlList(value);
  if (urls.length === 0) return false;
  const text = extractTextValue(value);
  if (!text) return true;
  const normalized = text.replace(/\s+/g, '');
  const urlJoined = urls.join(',');
  return normalized === urlJoined.replace(/\s+/g, '') || urls.every((u) => text.includes(u));
}

export function coercePrefillUrlList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((x) => {
        if (typeof x === 'string') return x.trim();
        if (x && typeof x === 'object') {
          const obj = x as Record<string, unknown>;
          const candidate = obj.url ?? obj.src ?? obj.href ?? obj.value;
          return typeof candidate === 'string' ? candidate.trim() : extractScalarText(x);
        }
        return extractScalarText(x);
      })
      .filter((u) => HTTP_URL_PATTERN.test(u));
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (HTTP_URL_PATTERN.test(trimmed)) return [trimmed];
    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((u) => HTTP_URL_PATTERN.test(u));
  }
  return [];
}
